// The currency FILTER control — "show me one currency's rows, or all of
// them" — shared by every list that narrows by currency.
//
// It exists because a filter that names currencies by their wire code
// contradicts the table underneath it: the chips, the balances and the
// detail pages all render the operator's CONFIGURED identity (SM-460 —
// name, symbol and artwork set on /currencies), so a picker listing
// "RED / GREEN / GOLD" is the one place in the console where renaming a
// currency does nothing. Options here carry the resolved presentation,
// which is also the fastest way for an operator to see, without
// reading, which money they are looking at.
//
// The "all" row is a real option rather than <Select>'s placeholder
// pseudo-option so a caller can decide what "all" means in ITS url:
// a page whose default is "no filter" writes an empty value, and a page
// whose default is a specific currency (Treasury → User Wallets, whose
// default is GOLD) needs "all" to be a value it can actually persist —
// absent would read back as the default.

import { Select } from "@/components/ui/Select";
import { CurrencyMonogram } from "@/components/ui/CurrencyAmount";
import { useCurrencySettings } from "@/components/currency/CurrencyPresentationProvider";
import {
  resolveCurrencyPresentation,
  type CurrencyCode,
  type CurrencyPresentationSettings,
} from "@/currency";

/**
 * How one currency reads in a picker: the configured NAME, the
 * configured SYMBOL when it is not simply the code again, and the code
 * itself.
 *
 * All three, because they answer different questions. The name is what
 * the rest of the console shows; the symbol is what sits beside the
 * figures an operator is about to reconcile; the code is what a support
 * ticket, a URL and an API call say. The symbol is dropped when it
 * equals the code so the seeded state reads "Gold (GOLD)" rather than
 * "Gold · GOLD (GOLD)".
 */
export function currencyOptionLabel(
  code: CurrencyCode,
  settings: CurrencyPresentationSettings | undefined,
): string {
  const { displayName, symbol } = resolveCurrencyPresentation(code, settings);
  const trimmed = symbol.trim();
  return trimmed && trimmed.toUpperCase() !== code
    ? `${displayName} · ${trimmed} (${code})`
    : `${displayName} (${code})`;
}

export function CurrencyFilterSelect<C extends CurrencyCode>({
  codes,
  value,
  onChange,
  label = "Currency",
  allLabel = "All currencies",
  allValue = "",
  className,
}: {
  /** The currencies this list can actually be narrowed to, in display order. */
  codes: readonly C[];
  /** The selected currency, or undefined for "all". */
  value: C | undefined;
  onChange: (next: C | undefined) => void;
  label?: string;
  allLabel?: string;
  /** The value the "all" row carries — see the note at the top of the file. */
  allValue?: string;
  className?: string;
}) {
  const settings = useCurrencySettings();
  return (
    <Select
      className={className}
      label={label}
      value={value ?? allValue}
      // Read back through `codes` rather than cast: whatever the control
      // reports is either one of the currencies this list offers or it
      // is "all", and there is no third case to invent.
      onChange={(e) => onChange(codes.find((c) => c === e.target.value))}
      options={[
        { value: allValue, label: allLabel },
        ...codes.map((code) => ({
          value: code,
          label: currencyOptionLabel(code, settings),
          icon: <CurrencyMonogram currency={code} />,
        })),
      ]}
    />
  );
}
